import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Clock, XCircle, Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export function EmailVerificationBanner() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [resending, setResending] = useState(false);

  if (!user || user.emailVerified) return null;

  const handleResend = async () => {
    setResending(true);
    try {
      await apiRequest("POST", "/api/auth/resend-verification");
      toast({
        title: "Email Sent",
        description: "A new verification email has been sent. Please check your inbox.",
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: "Failed to resend verification email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <Alert variant="default" className="border-blue-500 bg-blue-50 dark:bg-blue-950/20 mb-4" data-testid="banner-email-verification">
      <Mail className="h-4 w-4 text-blue-600" />
      <AlertTitle className="text-blue-800 dark:text-blue-200">Verify Your Email</AlertTitle>
      <AlertDescription className="text-blue-700 dark:text-blue-300">
        <span>Please check your inbox and click the verification link we sent to <strong>{user.email}</strong>.</span>
        <Button
          variant="outline"
          size="sm"
          className="ml-3"
          onClick={handleResend}
          disabled={resending}
          data-testid="button-resend-verification-banner"
        >
          {resending ? (
            <>
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Sending...
            </>
          ) : (
            "Resend Email"
          )}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

export function VerificationBanner() {
  const { user } = useAuth();

  const { data: verificationSetting, isLoading: settingLoading } = useQuery<{ verificationRequired: boolean }>({
    queryKey: ['/api/site-settings/verification-required'],
  });

  if (!user || settingLoading) return null;

  if (verificationSetting?.verificationRequired === false) return null;

  const status = user.verificationStatus || "pending";

  if (status === "verified") return null;

  if (status === "pending") {
    return (
      <Alert variant="default" className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20 mb-4" data-testid="banner-verification-pending">
        <Clock className="h-4 w-4 text-yellow-600" />
        <AlertTitle className="text-yellow-800 dark:text-yellow-200">Account Pending Verification</AlertTitle>
        <AlertDescription className="text-yellow-700 dark:text-yellow-300">
          Your account is currently under review. You will have full access to all features once an admin verifies your profile.
          This typically takes 1-2 business days.
        </AlertDescription>
      </Alert>
    );
  }

  if (status === "rejected") {
    return (
      <Alert variant="destructive" className="mb-4" data-testid="banner-verification-rejected">
        <XCircle className="h-4 w-4" />
        <AlertTitle>Account Verification Rejected</AlertTitle>
        <AlertDescription>
          Your account verification was not approved.
          {user.verificationNote && (
            <span className="block mt-1">
              <strong>Reason:</strong> {user.verificationNote}
            </span>
          )}
          <span className="block mt-1">
            Please contact support if you believe this is an error.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}

export function useVerificationStatus() {
  const { user } = useAuth();

  const { data: verificationSetting, isLoading: settingLoading } = useQuery<{ verificationRequired: boolean }>({
    queryKey: ['/api/site-settings/verification-required'],
  });
  
  const verificationOff = verificationSetting?.verificationRequired === false;
  const status = user?.verificationStatus || "pending";
  const isVerified = verificationOff || status === "verified";
  const isPending = !verificationOff && status === "pending";
  const isRejected = !verificationOff && status === "rejected";
  
  return {
    status,
    isVerified,
    isPending,
    isRejected,
    canPost: isVerified,
    canMessage: isVerified,
    isLoading: settingLoading,
  };
}
